import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "@tabipla/components";
import { useConfirmDialog } from "@tabipla/hooks/confirm-dialog";
import {
  useDeleteSpot,
  useUpdateTravelMode,
  useCalculateTravelTimes,
  useReorderSpots,
} from "@/application/hooks/useSpots";
import { useRegenerateDay } from "@/application/hooks/useTrips";
import { useSaveToFavorites } from "@/application/hooks/useFavorites";
import { useSaveToMekke, useMekkeStatus } from "@/application/hooks/useIntegration";
import { toast } from "sonner";
import type { SpotResponseDto, TravelMode } from "@tabipla/shared";
import type { ItineraryDayCardContainerProps } from "./types";
import { ItineraryDayCard } from "./ItineraryDayCard";

/**
 * ItineraryDayCardContainer - handles all data fetching, mutations, and state management.
 * Follows Container/Presenter pattern for better testability and separation of concerns.
 */
export function ItineraryDayCardContainer({
  tripId,
  itinerary,
  viewMode,
  onAddSpot,
  onEditSpot,
}: ItineraryDayCardContainerProps) {
  const { t } = useTranslation();
  const { confirm, dialogProps } = useConfirmDialog();

  // ==================== Mutation Hooks ====================
  const deleteMutation = useDeleteSpot(tripId);
  const regenerateMutation = useRegenerateDay(tripId);
  const saveFavoriteMutation = useSaveToFavorites();
  const saveToMekkeMutation = useSaveToMekke();
  const updateTravelModeMutation = useUpdateTravelMode(tripId);
  const calculateTravelTimesMutation = useCalculateTravelTimes(tripId, itinerary.id);
  const reorderMutation = useReorderSpots(tripId, itinerary.id);

  // ==================== Query Hooks ====================
  const spotIds = useMemo(() => itinerary.spots.map((s) => s.id), [itinerary.spots]);
  const { data: mekkeStatus } = useMekkeStatus(spotIds);
  const savedToMekkeSpotIds = useMemo(
    () => new Set(mekkeStatus?.savedSpotIds ?? []),
    [mekkeStatus?.savedSpotIds]
  );

  // ==================== Handlers ====================
  const handleRegenerate = useCallback(async () => {
    const confirmed = await confirm({
      title: t("itinerary.regenerateDay"),
      description: t("itinerary.regenerateConfirm", { day: itinerary.dayNumber }),
      confirmText: t("common.regenerate"),
      cancelText: t("common.cancel"),
    });
    if (confirmed) {
      regenerateMutation.mutate({ dayNumber: itinerary.dayNumber });
    }
  }, [confirm, t, itinerary.dayNumber, regenerateMutation]);

  const handleDelete = useCallback(
    async (spotId: string) => {
      const confirmed = await confirm({
        title: t("spots.deleteSpot"),
        description: t("spots.deleteConfirm"),
        confirmText: t("common.delete"),
        cancelText: t("common.cancel"),
        destructive: true,
      });
      if (confirmed) {
        deleteMutation.mutate(spotId);
      }
    },
    [confirm, t, deleteMutation]
  );

  const handleSaveToFavorites = useCallback(
    async (spot: SpotResponseDto) => {
      try {
        await saveFavoriteMutation.mutateAsync({
          name: spot.name,
          description: spot.description,
          category: spot.category as
            | "sightseeing"
            | "food"
            | "activity"
            | "transport"
            | "accommodation",
          location: spot.location,
          placeId: spot.placeId,
          latitude: spot.latitude,
          longitude: spot.longitude,
          formattedAddress: spot.formattedAddress,
          cost: spot.cost,
          notes: spot.notes,
        });
        toast.success(t("favorites.savedToFavorites"));
      } catch {
        toast.error(t("favorites.saveFailed"));
      }
    },
    [saveFavoriteMutation, t]
  );

  const handleSaveToMekke = useCallback(
    async (spot: SpotResponseDto) => {
      try {
        await saveToMekkeMutation.mutateAsync({
          spotId: spot.id,
          tripId,
        });
        toast.success(t("spots.savedToMekke"));
      } catch {
        toast.error(t("spots.saveToMekkeFailed"));
      }
    },
    [saveToMekkeMutation, tripId, t]
  );

  const handleAddSpot = useCallback(
    (startTime?: string) => {
      onAddSpot(itinerary.id, startTime);
    },
    [onAddSpot, itinerary.id]
  );

  const handleTravelModeChange = useCallback(
    (spotId: string, mode: TravelMode) => {
      updateTravelModeMutation.mutate({ spotId, travelMode: mode });
    },
    [updateTravelModeMutation]
  );

  const handleCalculateTravelTimes = useCallback(() => {
    calculateTravelTimesMutation.mutate("driving");
  }, [calculateTravelTimesMutation]);

  const handleReorder = useCallback(
    (spotIds: string[]) => {
      reorderMutation.mutate(spotIds);
    },
    [reorderMutation]
  );

  // ==================== Render ====================
  return (
    <>
      <ItineraryDayCard
        itinerary={itinerary}
        savedToMekkeSpotIds={savedToMekkeSpotIds}
        viewMode={viewMode}
        isRegenerating={regenerateMutation.isPending}
        onAddSpot={handleAddSpot}
        onEditSpot={onEditSpot}
        onDelete={handleDelete}
        onRegenerate={handleRegenerate}
        onSaveToFavorites={handleSaveToFavorites}
        onSaveToMekke={handleSaveToMekke}
        onTravelModeChange={handleTravelModeChange}
        onCalculateTravelTimes={handleCalculateTravelTimes}
        isCalculatingTravelTimes={calculateTravelTimesMutation.isPending}
        onReorder={handleReorder}
      />
      <ConfirmDialog {...dialogProps} />
    </>
  );
}
